import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

//Components
import FloatingLabelInput from '~components/FloatingLabelInput';
import FloatingLabelPasswordField from '~components/FloatingLabelPasswordField';
import ResultScreen from '~components/ResultScreen';
//Utils
import { getInputStates, parseResponseErrors } from '~utils/formHelpers';
//Validators
import { loginSchema } from '~utils/formValidators';
//GraphQL
import { LoginUserMutation, useLoginUserMutation } from '~graphqlResources';
//Context
import { useAuthContext } from '~context/auth';
//Types
import { AuthLoadingState } from '~context/auth/types';
//Hooks
import useOnGraphQLError from '~hooks/useOnGraphQLErrors';



const EMAIL_NOT_CONFIRMED_ERROR = 'email not confirmed';

export interface ILoginFormInputs {
    email: string;
    password: string;
}

interface ILoginFormProps {
    onSuccess(response: LoginUserMutation['login']): void;
}

export default function LoginForm({ onSuccess }: ILoginFormProps) {
    const [failScreenIsVisible, setFailScreenIsVisible] = useState(false);
    const {
        register,
        handleSubmit,
        formState,
        setError
    } = useForm<ILoginFormInputs>({
        resolver: yupResolver(loginSchema)
    });
    const { login, setAuthLoadingState } = useAuthContext();
    const handleGraphQLError = useOnGraphQLError<ILoginFormInputs>();

    const { isValid, isSubmitted } = formState;

    const [loginUser, { loading: mutationLoading }] = useLoginUserMutation({
        update(
            _,
            {
                data: {
                    login: { profile, jwt, subscription }
                }
            }
        ) {
            if (jwt && profile) {
                login({ profile, jwt, subscription });
                onSuccess({ profile, jwt });
            }
        },
        onError(err) {
            const formErrors = parseResponseErrors(err);

            if (formErrors.code === 'GRAPHQL_VALIDATION_FAILED') {
                const firstErrorField: string = Object.keys(
                    formErrors.errors
                )?.[0];
                const firstErrorMessage: string = Object.values(
                    formErrors.errors
                )?.[0] as string;
                if (
                    firstErrorField === 'email' ||
                    firstErrorField === 'password'
                ) {
                    setError('email', { type: 'manual', message: '' });
                    setError('password', {
                        type: 'manual',
                        message: 'Email and/or password are incorrect'
                    });

                    if (
                        firstErrorMessage?.[0]?.toLowerCase?.() ===
                        EMAIL_NOT_CONFIRMED_ERROR
                    ) {
                        setFailScreenIsVisible(true);
                    }
                } else {
                    handleGraphQLError()(err);
                }
            } else {
                handleGraphQLError()(err);
            }

            setAuthLoadingState(AuthLoadingState.FAILED);
        }
    });

    const onSubmit = (data: ILoginFormInputs) => {
        const { email, password } = data;

        setAuthLoadingState(AuthLoadingState.PENDING);

        loginUser({
            variables: {
                email,
                password
            }
        });
    };

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <FloatingLabelInput
                    {...getInputStates('email', formState)}
                    name="email"
                    autoComplete="email"
                    register={register}
                    label="Email"
                    type="email"
                    id="email"
                />

                <FloatingLabelPasswordField
                    {...getInputStates('password', formState)}
                    name="password"
                    autoComplete="current-password"
                    register={register}
                    label="Password"
                    id="password"
                />

                <button
                    type="submit"
                    className="btn"
                    disabled={(isSubmitted && !isValid) || mutationLoading}
                >
                    Login
                </button>
            </form>

            {failScreenIsVisible && (
                <ResultScreen
                    className="auth-fail-screen"
                    result="fail"
                    title="Email not confirmed"
                >
                    <div>
                        Please check your email for verification link (check
                        also spam folder)
                    </div>
                </ResultScreen>
            )}
        </>
    );
}
